import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  IconButton,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import {
  AddAutocomplete,
  AddButton,
  ControlsContainer,
  EntitlementsHeader,
  ScrollableTable,
  ScrollableTableContainer
} from "components/Settings/RolesSectionSharedStyles";
import DeleteIcon from "@mui/icons-material/Delete";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useLoadCounter } from "hooks/useLoadCounter";

export function RoleEntitlementsSection(props) {
  const [addedEntitlement, setAddedEntitlement] = useState(null);

  const {
    canEditRoleEntitlements,
    updateRole,
    role,
    entitlements,
    loading,
    error
  } = props;
  const tableLoadCount = useLoadCounter(loading);

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const mappedEntitlements =
    role?.entitlements?.map(id =>
      entitlements?.find(entitlement => entitlement.id === id)
    ) ?? [];

  const getEntitlementName = id => {
    return entitlements?.find(entitlement => entitlement.id === id)?.name;
  };

  const addEntitlementFromRole = id => {
    const entitlementName = getEntitlementName(id);

    if (!canEditRoleEntitlements) {
      addSnackbarToQueue({
        severity: "error",
        message: `Can not add ${entitlementName} to ${role?.name} as you do not have required permission.`
      });
      return;
    }

    if (role?.entitlements) {
      const updatedEntitlements = !role.entitlements.includes(id)
        ? [...role.entitlements, id]
        : role.entitlements;

      updateRole({ entitlements: updatedEntitlements });
      setAddedEntitlement(null);
    }
  };

  const deleteEntitlementFromRole = id => {
    const entitlementName = getEntitlementName(id);

    if (!canEditRoleEntitlements) {
      addSnackbarToQueue({
        severity: "error",
        message: `Can not remove ${entitlementName} to ${role?.name} as you do not have required permission.`
      });
      return;
    }

    if (role?.entitlements) {
      const updatedEntitlements = role.entitlements.filter(
        entitlement => entitlement !== id
      );
      updateRole({ entitlements: updatedEntitlements });
    }
  };

  return (
    <>
      <EntitlementsHeader>Entitlements</EntitlementsHeader>
      <ControlsContainer>
        <AddAutocomplete
          label="Add Entitlement"
          size="small"
          autoHighlight
          autoComplete
          disabled={!canEditRoleEntitlements}
          aria-label="add entitlement autocomplete"
          options={entitlements?.filter(
            ({ id }) => !role?.entitlements?.includes(id) ?? []
          )}
          getOptionLabel={option => option?.name}
          value={addedEntitlement}
          onChange={(_, value) => setAddedEntitlement(value)}
        />
        <AddButton
          variant="contained"
          color="primary"
          disabled={!addedEntitlement || !canEditRoleEntitlements}
          onClick={() => addEntitlementFromRole(addedEntitlement.id)}
        >
          Add Entitlement
        </AddButton>
      </ControlsContainer>
      <ScrollableTableContainer component={Paper}>
        <ProgressIndicatorContainer loading={loading} error={error}>
          <PerfectScrollbar>
            <ScrollableTable stickyHeader aria-label="role entitlements table">
              <TableHead>
                <TableRow>
                  <TableCell data-col={1}>ID</TableCell>
                  <TableCell data-col={2}>Name</TableCell>
                  <TableCell data-col={3}>Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                data-qa_loading={loading}
                data-qa_load_count={tableLoadCount}
              >
                {mappedEntitlements.map(entitlement =>
                  entitlement ? (
                    <TableRow
                      key={entitlement.id}
                      aria-label={`entitlement row ${entitlement.id}`}
                    >
                      <TableCell data-col={1}>{entitlement.id}</TableCell>
                      <TableCell data-col={2}>
                        <Link
                          to={`/pages/settings/entitlement/${entitlement.id}`}
                        >
                          {entitlement.name}
                        </Link>
                      </TableCell>
                      <TableCell data-col={3}>
                        <IconButton
                          disabled={!canEditRoleEntitlements}
                          onClick={() =>
                            deleteEntitlementFromRole(entitlement.id)
                          }
                          size="large"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ) : null
                )}
              </TableBody>
            </ScrollableTable>
          </PerfectScrollbar>
        </ProgressIndicatorContainer>
      </ScrollableTableContainer>
    </>
  );
}

RoleEntitlementsSection.propTypes = {
  role: PropTypes.shape({
    id: PropTypes.number,
    owners: PropTypes.array,
    name: PropTypes.string,
    entitlements: PropTypes.array,
    users: PropTypes.array
  }),
  updateRole: PropTypes.func,
  entitlements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })
  ),
  canEditRoleEntitlements: PropTypes.bool,
  error: PropTypes.bool,
  loading: PropTypes.bool
};
